import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { solveMto, calculatePathDistance } from '../../../Services';
import { processVehiclePaths } from '../utils';

export const useSolveMto = (setPath, setSolveResult) => {
  const [isSolving, setIsSolving] = useState(false);
  const [solveError, setSolveError] = useState('');

  // Calculate total time based on connections
  const calculatePathTotalTime = (path, connections) => {
    if (!path || path.length < 2 || !connections || connections.length === 0) {
      return 0;
    }

    let totalTime = 0;
    
    // Build connection lookup map
    const connectionMap = new Map();
    connections.forEach(conn => {
      const key1 = `${conn.points[0]?.id}-${conn.points[1]?.id}`;
      const key2 = `${conn.points[1]?.id}-${conn.points[0]?.id}`;
      connectionMap.set(key1, conn.duration || 0);
      connectionMap.set(key2, conn.duration || 0);
    });

    // Calculate total time for the path
    for (let i = 0; i < path.length - 1; i++) {
      const fromId = path[i];
      const toId = path[i + 1];
      const connectionKey = `${fromId}-${toId}`;
      
      const duration = connectionMap.get(connectionKey);
      if (duration !== undefined) {
        totalTime += duration;
      }
    }

    return totalTime;
  };

  // Auto solve path function
  const solvePath = async (locations, startId, endId, connections, selectedAlgorithm, demand = 0, modeCapacities = {}) => {
    // Validate if start point is set
    if (!startId) {
      setSolveError('Please set a start point first');
      return;
    }
    
    // Validate if end point is set
    if (!endId) {
      setSolveError('Please set an end point first');
      return;
    }
    
    // Check if start and end points are different
    if (startId === endId) {
      setSolveError('Start point and end point must be different');
      return;
    }
    
    // Check number of location points
    if (locations.length < 2) {
      setSolveError('At least 2 location points are needed to calculate path');
      return;
    }
    
    // Check if connections are provided for connection-based algorithms
    if (!connections || connections.length === 0) {
      setSolveError('Please create connections in Connection Mode first. This algorithm requires predefined connections between points.');
      return;
    }
    
    // For capacity-constrained algorithm, validate capacities are set
    if (demand > 0 && selectedAlgorithm === 'capacity_constrained_shortest_path') {
      // Get unique transport modes used in connections
      const usedModes = [...new Set(connections.map(conn => conn.transportMode))];
      
      // Check if all used modes have capacity set
      const missingCapacities = usedModes.filter(mode => {
        const capacity = modeCapacities[mode];
        return !capacity || capacity <= 0;
      });
      
      if (missingCapacities.length > 0) {
        const modeNames = {
          'electric_motorcycle': 'Bike',
          'truck': 'Truck',
          'railway': 'Train',
          'shipping': 'Ship'
        };
        const missingNames = missingCapacities.map(m => modeNames[m] || m).join(', ');
        setSolveError(`Please set capacity for the following transport modes in Connection Mode: ${missingNames}`);
        return;
      }
    }
    
    setSolveError('');
    setIsSolving(true);
    
    try {
      // Generate a unique group ID
      const groupId = `vrp_${uuidv4()}`;

      // Call MTO solving API with connection-aware optimization
      const result = await solveMto(
        groupId, 
        locations, 
        startId, 
        endId,
        connections,
        selectedAlgorithm,
        true,  // Use GeoJSON format
        demand,
        modeCapacities
      );

      // Process the result data using utility function
      const {
        processedPath,
        pathMetadata,
        processedResult
      } = processVehiclePaths(result);
      
      // MTO uses hours as the only time unit - no conversion needed
      let totalTime;
      if (result.time !== undefined && result.time !== null) {
        // Use backend's duration (already in hours)
        totalTime = result.time;
      } else {
        // Fallback: calculate from connections
        totalTime = calculatePathTotalTime(processedPath, connections);
      }
      
      // Create enhanced result with appropriate total time
      const enhancedResult = {
        ...processedResult,
        totalTime: totalTime,
        path: processedPath,
        algorithm: selectedAlgorithm
      };
      
      // Set path with metadata if available
      if (pathMetadata) {
        setPath(processedPath, pathMetadata);
      } else {
        setPath(processedPath);
      }
      
      // Set solve result with connection-based total time
      setSolveResult({
        ...enhancedResult,
        totalTime: totalTime
      });

    } catch (error) {
      console.error('Solve failed:', error);
      setSolveError(error.message || 'Solve failed');
    } finally {
      setIsSolving(false);
    }
  };


  return {
    isSolving,
    solveError,
    solvePath
  };
}; 